import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormViewMode, useFormIndexActions } from '@atlassian/proforma-form-system';
import type { Form } from '@atlassian/proforma-form-system/add-form';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import type { IssueViewStore } from '../../stores/issue-view-store.tsx';
import { useSelectFormNoDiscardChangeNotice } from '../use-handle-form-selected/index.tsx';

export const useAddForm = (
	issueViewStore: IssueViewStore,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setIsIndexLoading, loadIndex, setFormViewMode, reportError } = useFormIndexActions();

	const selectFormNoDiscardChangeNotice = useSelectFormNoDiscardChangeNotice(
		issueViewStore.loadFormStore,
		disableIssueViewFullPageDropzoneCallback,
	);

	return useCallback(
		async (formToAdd: Form) => {
			const templateFormId = formToAdd.value;
			setIsIndexLoading(true);

			try {
				const form = await issueViewStore.callAddForm(templateFormId);

				await loadIndex();

				setIsIndexLoading(true);
				await selectFormNoDiscardChangeNotice(form.id, issueViewStore.createFormStore(form));
				setFormViewMode(FormViewMode.Edit);

				fireTrackAnalytics(createAnalyticsEvent({}), `${ActionSubject.FORM} ${Action.CREATED}`, {
					formId: form.id,
					templateFormId,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				reportError(error, { id: 'addForm', attributes: { templateFormId } });
				return Promise.reject(error);
			} finally {
				setIsIndexLoading(false);
			}
		},
		[
			setIsIndexLoading,
			issueViewStore,
			loadIndex,
			createAnalyticsEvent,
			selectFormNoDiscardChangeNotice,
			setFormViewMode,
			reportError,
		],
	);
};
